"use client";

import * as React from "react";

import {
  Layers3,
} from "lucide-react";

import {
  getSixPillarsPreviewPerson,
  type PreviewPerson,
} from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

export type InsightTone = "neutral" | "positive" | "warning" | "info";

const toneStyles: Record<
  InsightTone,
  {
    badgeClassName: string;
  }
> = {
  neutral: {
    badgeClassName: "border-slate-200 bg-white text-slate-700",
  },
  positive: {
    badgeClassName: "border-emerald-200 bg-emerald-100/80 text-emerald-900",
  },
  warning: {
    badgeClassName: "border-amber-200 bg-amber-100/80 text-amber-900",
  },
  info: {
    badgeClassName: "border-sky-200 bg-sky-100/80 text-sky-900",
  },
};

export function InsightDetailLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 py-3 md:grid-cols-[7rem_minmax(0,1fr)] md:gap-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        {label}
      </p>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function DetailLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 py-3.5 md:grid-cols-[10rem_minmax(0,1fr)] md:gap-5">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function OrdinalPill({ index }: { index: number }) {
  return (
    <div className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/70 bg-background text-xs font-semibold tracking-[0.08em] text-foreground/72 shadow-[0_10px_20px_-18px_rgba(15,23,42,0.16)]">
      {String(index + 1).padStart(2, "0")}
    </div>
  );
}

export function SurfaceLaneBadge({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold tracking-[0.02em]",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function EvidencePill({ count }: { count: number }) {
  return (
    <span className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border/70 bg-background px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-[0_8px_20px_-18px_rgba(15,23,42,0.16)] transition-colors duration-150 hover:bg-muted/22 hover:text-foreground/82 animate-[roadmap-attention-pulse_2.35s_ease-in-out_infinite]">
      <Layers3 className="size-3.5 text-foreground/66" />
      <span>Evidence</span>
      <MiniCount>{count}</MiniCount>
    </span>
  );
}

export function AccordionChevronCircle({
  children,
  groupClassName,
}: React.PropsWithChildren<{ groupClassName?: string }>) {
  return (
    <span
      className={cn(
        "mt-1 flex shrink-0 items-center justify-center text-foreground/42 transition-colors duration-200",
        groupClassName,
      )}
    >
      {children}
    </span>
  );
}

export function MiniOutlineBadge({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function MiniBadge({ children }: React.PropsWithChildren) {
  return (
    <span className="inline-flex items-center rounded-full border border-border/70 bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {children}
    </span>
  );
}

export function MiniCount({ children }: React.PropsWithChildren) {
  return (
    <span className="inline-flex min-w-5 items-center justify-center rounded-full border border-border/70 bg-muted/35 px-1 text-[10px] font-semibold text-foreground/82">
      {children}
    </span>
  );
}

export function AreaScoreBadge({
  children,
  tone,
}: React.PropsWithChildren<{ tone: InsightTone }>) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-[0.02em]",
        toneStyles[tone].badgeClassName,
      )}
    >
      {children}
    </span>
  );
}

export function MetricBlock({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div>
      <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-[1.02rem] font-semibold tracking-[-0.03em] text-foreground">
        {value}
      </p>
    </div>
  );
}

export function fundamentalsAverageTone(value: number): InsightTone {
  if (value < 3) {
    return "warning";
  }

  if (value < 4) {
    return "warning";
  }

  return "positive";
}

export function getRatioPercent(completed: number, total: number) {
  if (!total) {
    return 0;
  }

  return Math.round((completed / total) * 100);
}

export function formatWhyThisOwner(text: string, ownerName: string) {
  const person = getSixPillarsPreviewPerson(ownerName);

  if (!person) {
    return text;
  }

  const normalized = text.trim();

  if (normalized.startsWith(person.name)) {
    const subject =
      person.pronouns.subject.charAt(0).toUpperCase()
      + person.pronouns.subject.slice(1);

    return `${subject}${normalized.slice(person.name.length)}`;
  }

  return normalized;
}

export function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export const avatarToneClasses: Record<PreviewPerson["avatarTone"], string> = {
  sand: "bg-[radial-gradient(circle_at_30%_30%,#fef3c7_0%,#f5d0a6_42%,#d9b087_100%)]",
  sky: "bg-[radial-gradient(circle_at_30%_30%,#dbeafe_0%,#93c5fd_42%,#6b9ee8_100%)]",
  rose: "bg-[radial-gradient(circle_at_30%_30%,#ffe4e6_0%,#fda4af_42%,#e28b93_100%)]",
  mint: "bg-[radial-gradient(circle_at_30%_30%,#dcfce7_0%,#86efac_42%,#6fc48a_100%)]",
  violet: "bg-[radial-gradient(circle_at_30%_30%,#ede9fe_0%,#c4b5fd_42%,#9f8ce1_100%)]",
};
